import { Injectable } from '@angular/core';
import { darkenRgba, hexToRgba } from '@digilize/shared/utils/helpers/src';
import { AppContext } from '@plys/src/application/app.context';
import { SeatsService } from '@plys/src/infrastructure/seats.service';
import { Ticket } from '@plys/src/models/ticket';
import { BehaviorSubject, filter, forkJoin, map, Observable, shareReplay, tap } from 'rxjs';

import { Seat, SeatType } from '../../models/seat';

@Injectable({
  providedIn: 'root',
})
export class SeatsState {
  private readonly maxSeats = new BehaviorSubject(10);
  private _selected = new Set<string>();
  private _taken = new Set<string>();
  private readonly reservedSeats = new BehaviorSubject<string[]>(null);
  private readonly gap = new BehaviorSubject<Seat | null>(null);
  private readonly types = new BehaviorSubject<SeatType[]>([]);
  private readonly _seatData = new Map<string, Seat>();

  public readonly seatType = new Map<string, string>();

  private catalogue: Observable<unknown> | null = null;
  private catalogueKey: string | null = null;

  constructor(private appContext: AppContext, private seatsService: SeatsService) {}

  get selected(): Set<string> {
    return this._selected;
  }

  get taken(): Set<string> {
    return this._taken;
  }

  /*
   * Seats reserved by anyone for the show, excluding the user's own selection
   */
  get taken$(): Observable<string[]> {
    return this.reservedSeats.asObservable().pipe(
      filter((taken) => !!taken),
      map((taken) => taken.filter((seatId) => !this.has(seatId))),
      tap((taken) => (this._taken = new Set(taken)))
    );
  }

  get gap$(): Observable<Seat | null> {
    return this.gap.asObservable();
  }

  get max$(): Observable<number> {
    return this.maxSeats.asObservable();
  }

  get types$(): Observable<SeatType[]> {
    return this.types.asObservable();
  }

  get max(): number {
    return this.maxSeats.value;
  }

  init(tickets: Ticket[] | null): void {
    const { seatsLimit } = this.appContext.get();
    this.maxSeats.next(seatsLimit > 0 ? seatsLimit : 10);
    if (tickets) {
      const seatsFromTickets = tickets.filter((ticket) => !!ticket.seat).map((ticket) => ticket.seat.id);

      this._selected = new Set(seatsFromTickets);
    }
  }

  fetchTaken(showId: string): Observable<string[]> {
    return this.seatsService.getReservations(showId).pipe(
      map((items) => (items ? items?.map((item) => item.seat_import_id) : [])),
      tap((seats) => {
        this.reservedSeats.next(seats);
      })
    );
  }

  removeTaken(seatId: string): void {
    const currentTakenSeats = this.reservedSeats.getValue();
    if (!currentTakenSeats) return;
    const updatedTakenSeats = currentTakenSeats.filter((id) => id !== seatId);
    this.reservedSeats.next(updatedTakenSeats);
  }

  getSeatData(id: string): Seat | undefined {
    return this._seatData.get(id);
  }

  /**
   * The seat catalogue and its types for one area, read once and shared. Whoever needs seat data
   * first starts it; everyone after joins that same read instead of paying for a second pair of
   * round trips — so it can be prefetched off the bootstrap's critical path.
   */
  loadCatalogue(shopId: string, areaId: string): Observable<unknown> {
    const key = `${shopId}:${areaId}`;
    if (!this.catalogue || this.catalogueKey !== key) {
      this.catalogueKey = key;
      this.catalogue = forkJoin([this.fetchSeats(shopId, areaId), this.fetchTypes(shopId, areaId)]).pipe(
        // a failed read must not be replayed to every later caller as a permanent failure
        tap({ error: () => (this.catalogue = null) }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.catalogue;
  }

  fetchSeats(shopId: string, areaId: string): Observable<string[]> {
    return this.seatsService.getSeats(shopId, areaId).pipe(
      tap((seats) => {
        seats.forEach((seat) => {
          this.seatType.set(seat.seat_import_id, seat.seat_type_id);
          this._seatData.set(
            seat.seat_import_id,
            new Seat(
              seat.seat_import_id,
              seat.seat_type_id,
              seat.row,
              seat.row_label,
              seat.column,
              seat.column_label,
              seat.group
            )
          );
        });
      }),
      map((seats) => seats.map((seat) => seat.id))
    );
  }

  fetchTypes(shopId: string, areaId: string): Observable<SeatType[]> {
    return this.seatsService.getTypes(shopId, areaId).pipe(
      tap((types) => {
        this.setTypes(types);
      })
    );
  }

  setTypes(types: SeatType[]): void {
    const withDarkened = types.map((type) => {
      const calculatedTakenColorDark = darkenRgba(hexToRgba(type.colorDark));
      const calculatedTakenColorLight = darkenRgba(hexToRgba(type.colorLight));
      return {
        ...type,
        calculatedTakenColorDark,
        calculatedTakenColorLight,
      };
    });
    this.types.next(withDarkened);
  }

  setMaxSeats(maxSeats: number): void {
    this.maxSeats.next(maxSeats);
  }

  canAdd(id: string): boolean {
    return !this._selected.has(id) && this._selected.size !== this.max;
  }

  add(id: string): boolean {
    if (!this.canAdd(id)) {
      return false;
    }

    this._selected.add(id);
    return true;
  }

  remove(id: string): void {
    this._selected.delete(id);
  }

  has(id: string): boolean {
    return this._selected.has(id);
  }

  isEmpty(): boolean {
    return this._selected.size === 0;
  }

  /*
   * Check if there is a single gap between selected seats in the same row
   * @returns the seat in the gap or null if there is no gap
   */
  verifySingleGap(): Seat | null {
    const rows: { [key: string]: Seat[] } = {};

    this._selected.forEach((seatId) => {
      const seat = this._seatData.get(seatId);
      if (!seat) return;
      if (!rows[seat.row]) rows[seat.row] = [];
      rows[seat.row].push(seat);
    });

    this._taken.forEach((seatId) => {
      const seat = this._seatData.get(seatId);
      if (!seat) return;
      if (!rows[seat.row]) rows[seat.row] = [];
      rows[seat.row].push(seat);
    });

    for (const rowKey in rows) {
      const seats = rows[rowKey].sort((a, b) => a.seat - b.seat);

      for (let i = 1; i < seats.length; i++) {
        const bothTaken = this._taken.has(seats[i].id) && this._taken.has(seats[i - 1].id);

        if (bothTaken) {
          continue;
        }

        if (seats[i].seat - seats[i - 1].seat > 2) {
          this.gap.next(null);
          return null;
        }
        if (seats[i].seat - seats[i - 1].seat > 1) {
          const gapColumn = seats[i].seat - 1;
          const gapSeat = Array.from(this._seatData.values()).find(
            (s) => s.row === seats[i].row && s.seat === gapColumn
          );
          if (!gapSeat || this._taken.has(gapSeat.id)) {
            this.gap.next(null);
            return null;
          }
          this.gap.next(gapSeat);
          return gapSeat;
        }
      }
    }

    this.gap.next(null);
    return null;
  }
}
